import { Badge, Code, Flex, Grid, Table, Text } from "@mantine/core";
import { useShow } from "@refinedev/core";
import { Show } from "@refinedev/mantine";
import { DetailsCard } from "components/common/show/DetailsPair";
import { CancelSession } from "entities/cancel_flow";
import { colorsByBrand } from "entities/cancel_flow/brand";
import { colorsByOutcome } from "entities/cancel_flow/outcome";
import { colorsByOfferType } from "entities/cancel_flow/offerType";

export const CancelSessionShow = () => {
  const { queryResult } = useShow<CancelSession>();
  const { data, isLoading } = queryResult;
  const session = data?.data;

  if (!session) return <div />;

  return (
    <Show isLoading={isLoading} canEdit={false}>
      <Grid>
        <Grid.Col span={8}>
          <Flex gap="sm" direction="column">
            <DetailsCard title="Survey Response">
              {session.cancel_survey_response ? (
                <Flex direction="column" gap="xs">
                  <Text size="sm">
                    <Text weight="bold" component="span">Reason: </Text>
                    {session.cancellation_reason?.label ?? session.cancel_survey_response.cancellation_reason_id ?? "—"}
                  </Text>
                  <Text size="sm">
                    <Text weight="bold" component="span">Alternative Solution: </Text>
                    {session.cancel_survey_response.alternative_solution || "—"}
                  </Text>
                  <Text size="sm">
                    <Text weight="bold" component="span">Return Likelihood: </Text>
                    {session.cancel_survey_response.return_likelihood ?? "—"}
                  </Text>
                  <Text size="sm">
                    <Text weight="bold" component="span">Free Text: </Text>
                    {session.cancel_survey_response.free_text_comment || "—"}
                  </Text>
                  {session.cancel_survey_response.custom_reason_text && (
                    <Text size="sm">
                      <Text weight="bold" component="span">Custom Reason Text: </Text>
                      {session.cancel_survey_response.custom_reason_text}
                    </Text>
                  )}
                </Flex>
              ) : (
                <Text color="dimmed">No survey response</Text>
              )}
            </DetailsCard>

            <DetailsCard title="Offer Interactions">
              {session.offer_interactions && session.offer_interactions.length > 0 ? (
                <Table>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Value</th>
                      <th>Coupon</th>
                      <th>Reason</th>
                      <th>Accepted</th>
                      <th>Shown At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {session.offer_interactions.map((interaction) => (
                      <tr key={interaction.id}>
                        <td>
                          <Badge color={colorsByOfferType[interaction.offer_type]}>
                            {interaction.offer_type}
                          </Badge>
                        </td>
                        <td>{interaction.offer_value}</td>
                        <td>{interaction.chargebee_coupon_id || "—"}</td>
                        <td>
                          {interaction.offer_type === "discount"
                            ? (session.cancellation_reason?.label ?? "—")
                            : "—"}
                        </td>
                        <td>
                          <Badge color={interaction.accepted ? "green" : "gray"}>
                            {interaction.accepted ? "Yes" : "No"}
                          </Badge>
                        </td>
                        <td>
                          {interaction.shown_at
                            ? new Date(interaction.shown_at).toLocaleString()
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Text color="dimmed">No offer interactions</Text>
              )}
            </DetailsCard>

            <DetailsCard title="Watchlist Entry">
              {session.watchlist_entry ? (
                <Flex direction="column" gap="xs">
                  <Text size="sm">
                    <Text weight="bold" component="span">Status: </Text>
                    <Badge>{session.watchlist_entry.status}</Badge>
                  </Text>
                  <Text size="sm">
                    <Text weight="bold" component="span">Customer ID: </Text>
                    {session.watchlist_entry.chargebee_customer_id}
                  </Text>
                  <Text size="sm">
                    <Text weight="bold" component="span">Subscription ID: </Text>
                    {session.watchlist_entry.chargebee_subscription_id}
                  </Text>
                  <Text size="sm">
                    <Text weight="bold" component="span">MRR: </Text>
                    {session.watchlist_entry.subscription_mrr}
                  </Text>
                  <Text size="sm">
                    <Text weight="bold" component="span">Deflected At: </Text>
                    {session.watchlist_entry.deflected_at
                      ? new Date(session.watchlist_entry.deflected_at).toLocaleString()
                      : "—"}
                  </Text>
                  <Text size="sm">
                    <Text weight="bold" component="span">Resolved At: </Text>
                    {session.watchlist_entry.resolved_at
                      ? new Date(session.watchlist_entry.resolved_at).toLocaleString()
                      : "—"}
                  </Text>
                </Flex>
              ) : (
                <Text color="dimmed">No watchlist entry</Text>
              )}
            </DetailsCard>
          </Flex>
        </Grid.Col>

        <Grid.Col span={4}>
          <Flex direction="column" gap="sm">
            <DetailsCard title="ID">
              <Text>{session.id}</Text>
            </DetailsCard>
            <DetailsCard title="UUID">
              <Code>{session.uuid}</Code>
            </DetailsCard>
            <DetailsCard title="Brand">
              <Badge variant="filled" color={colorsByBrand[session.brand]}>
                {session.brand}
              </Badge>
            </DetailsCard>
            <DetailsCard title="Segment">
              <Text>{session.plan_segment}</Text>
            </DetailsCard>
            <DetailsCard title="Outcome">
              {session.outcome ? (
                <Badge variant="filled" color={colorsByOutcome[session.outcome]}>
                  {session.outcome}
                </Badge>
              ) : (
                <Badge color="gray">Pending</Badge>
              )}
            </DetailsCard>
            <DetailsCard title="MRR">
              <Text>{session.subscription_mrr || "—"}</Text>
            </DetailsCard>
            <DetailsCard title="Started At">
              <Code>{session.started_at ? new Date(session.started_at).toLocaleString() : "—"}</Code>
            </DetailsCard>
            <DetailsCard title="Completed At">
              <Code>{session.completed_at ? new Date(session.completed_at).toLocaleString() : "—"}</Code>
            </DetailsCard>
            <DetailsCard title="Created At">
              <Code>{new Date(session.created_at).toLocaleString()}</Code>
            </DetailsCard>
          </Flex>
        </Grid.Col>
      </Grid>
    </Show>
  );
};
